// src/data/projectsData.ts
import { Project } from '../types';

export const featuredProjects: Project[] = [
  {
    id: 1,
    title: 'Green Skills Project',
    slug: 'green-skills-project',
    category: 'youth_empowerment',
    description: 'Inspiring youth to promote sustainable agriculture and environmental awareness through hands-on training in kitchen gardening.',
    image: '/images/green-skills-project.jpg',
    impact: '500+ students trained',
    progress: 100,
    status: 'active',
    location: 'West Pokot County, Kenya',
    duration: '6 months',
    goals: 'Train 1000 youth in sustainable agriculture practices and environmental conservation techniques through hands-on workshops and community engagement.',
    full_content: `
      <p>The Green Skills Project is a youth-focused initiative that empowers young people with practical skills in sustainable agriculture and environmental stewardship.</p>
      <p>Through our comprehensive training programs, participants learn:</p>
      <ul>
        <li>Kitchen gardening and organic farming techniques</li>
        <li>Water conservation and management</li>
        <li>Composting and waste management</li>
        <li>Climate-smart agricultural practices</li>
      </ul>
      <p>Our approach combines theoretical knowledge with hands-on practical sessions, ensuring that youth can immediately apply what they learn in their communities.</p>
    `,
    challenges: 'Limited access to farming spaces in urban areas and seasonal climate changes affecting crop cycles.',
    success_stories: 'Over 500 students have started their own kitchen gardens, and 50% have initiated community farming projects.',
    volunteers_needed: 5,
    budget: 200,
    created_at: '2024-01-15'
  },
  {
    id: 2,
    title: 'Youth Climate Advocacy',
    slug: 'youth-climate-advocacy-project',
    category: 'climate_action',
    description: 'Using sports and education to raise awareness and drive action towards sustainable environmental practices.',
    image: '/images/youth-advocacy.jpg',
    impact: '1000+ youth engaged',
    progress: 100,
    status: 'active',
    location: 'Multiple locations across East Africa',
    duration: '1 year',
    goals: 'Mobilize youth as climate champions through innovative sports-based learning and advocacy campaigns.',
    full_content: `
      <p>The Youth Climate Advocacy program combines the power of sports with environmental education to create engaging learning experiences for young people.</p>
      <p>Key program components include:</p>
      <ul>
        <li>Climate action sports tournaments</li>
        <li>Environmental education workshops</li>
        <li>Community clean-up campaigns</li>
        <li>Youth-led advocacy initiatives</li>
      </ul>
      <p>We believe that sports can be a powerful vehicle for social change, and we're leveraging this to build a generation of environmentally conscious leaders.</p>
    `,
    challenges: 'Engaging youth consistently and measuring long-term behavioral change.',
    success_stories: 'Formed 20 youth climate clubs in schools and communities, organized 5 regional sports tournaments with climate themes.',
    volunteers_needed: 5,
    budget: 150,
    created_at: '2024-02-10'
  },
  {
    id: 3,
    title: "Women's Farming Collective",
    slug: 'womens-farming-collective-project',
    category: 'women_empowerment',
    description: 'Empowering women farmers with skills and resources for sustainable agriculture and market access.',
    image: '/images/women-farming.jpg',
    impact: '200+ women supported',
    progress: 100,
    status: 'active',
    location: 'Rural farming communities',
    duration: '8 months',
    goals: 'Enhance food security and economic independence for women farmers through sustainable agriculture training and market linkages.',
    full_content: `
      <p>The Women's Farming Collective focuses on empowering women in rural communities with the skills, resources, and market access needed to thrive as agricultural entrepreneurs.</p>
      <p>Our program offers:</p>
      <ul>
        <li>Sustainable farming techniques training</li>
        <li>Access to quality seeds and tools</li>
        <li>Market linkage and value addition training</li>
        <li>Financial literacy and business skills</li>
      </ul>
      <p>We work closely with women-led farming groups to build resilient agricultural enterprises that can withstand climate challenges and market fluctuations.</p>
    `,
    challenges: 'Cultural barriers and limited access to land ownership for women in some communities.',
    success_stories: '200 women have increased their crop yields by 60%, and 75% have started saving groups for financial resilience.',
    volunteers_needed: 1,
    budget: 250,
    created_at: '2024-03-05'
  }
];

// Helper function to get all projects
export const getAllProjects = (): Project[] => {
  return featuredProjects;
};

// Helper function to get project by slug
export const getProjectBySlug = (slug: string): Project | undefined => {
  return featuredProjects.find(project => project.slug === slug);
};

// Helper function to get related projects
export const getRelatedProjects = (currentProject: Project, limit: number = 3): Project[] => {
  return featuredProjects
    .filter(project => project.category === currentProject.category && project.id !== currentProject.id)
    .slice(0, limit);
};